import { useCallback, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera } from "lucide-react";
import { toast } from "sonner";

import useFinanceVisao from "@/hooks/useFinanceVisao";
import useDashboardData from "@/hooks/useDashboardData";
import { authFetch } from "@/lib/authFetch";

import HeroSaldoCard from "./HeroSaldoCard";
import SugestaoNexoVisaoCard from "./SugestaoNexoVisaoCard";
import AtividadeRecenteCard from "./AtividadeRecenteCard";
import AssinaturasResumoCard from "./AssinaturasResumoCard";
import FaturasCard from "./FaturasCard";
import FluxoCaixaChart from "./FluxoCaixaChart";
import DistribuicaoGastosDonut from "./DistribuicaoGastosDonut";
import RitmoGastosLineChart from "./RitmoGastosLineChart";
import MapaGastosHeatmap from "./MapaGastosHeatmap";
// A2 / fix-visibilidade — captura de despesa por foto.
// Wire foi movido de VisaoTabLegacy (código morto desde FinanceSection.jsx
// passou a renderizar VisaoTab) pro componente vivo.
import ReceiptCapture from "@/components/finance/ReceiptCapture";
import ReceiptConfirm from "@/components/finance/ReceiptConfirm";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// FIN-1 — VisaoTab orquestrador.
// Linhas 0-4 do brief (Sugestão / HeroSaldo / 3 cards / 2 charts / 2 charts).
// T6 entrega 0 e 1; T7-T9 preenchem 2-4.
//
// stagger 80ms entre linhas com Framer Motion, ease tokenizado.

const ROW_TRANSITION = { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] };

export default function VisaoTab({ onTalk }) {
  const visao = useFinanceVisao();
  const { briefing } = useDashboardData();

  // ── A2 (fix-visibilidade) — fluxo de despesa por foto ───────────
  const [photoStep, setPhotoStep] = useState(null); // "capture"|"confirm"|null
  const [photoExtracted, setPhotoExtracted] = useState(null);
  const [photoSubmitting, setPhotoSubmitting] = useState(false);

  const closePhoto = useCallback(() => {
    if (photoSubmitting) return;
    setPhotoStep(null);
    setPhotoExtracted(null);
  }, [photoSubmitting]);

  const registerPhotoExpense = useCallback(async (payload) => {
    // POST direto pra /api/finance/transactions (mesmo path do agent e
    // do modal manual antigo do VisaoTabLegacy). Resposta pode trazer
    // `{created: false, possible_duplicate}` — caímos no confirm nativo.
    const _post = (skip) =>
      authFetch(
        `${API_URL}/api/finance/transactions${skip ? "?skip_duplicate_check=true" : ""}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

    setPhotoSubmitting(true);
    try {
      let res = await _post(false);
      let body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error("Erro ao registrar despesa.");
        return;
      }
      if (body?.created === false && body?.possible_duplicate) {
        const ex = body.possible_duplicate.existing || {};
        const proceed = window.confirm(
          `Você já registrou R$ ${(ex.amount ?? 0).toFixed(2)} em ` +
          `"${ex.description || "sem descrição"}" em ${ex.date}. ` +
          "Confirma que é uma transação NOVA?",
        );
        if (!proceed) return;
        res = await _post(true);
        body = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast.error("Erro ao registrar (retry).");
          return;
        }
      }
      const valor = Number(payload.amount).toLocaleString("pt-BR", {
        style: "currency", currency: "BRL",
      });
      toast.success(`Despesa de ${valor} registrada.`);
      try { visao.refetch?.(); } catch { /* */ }
      setPhotoStep(null);
      setPhotoExtracted(null);
    } catch (_e) {
      toast.error("Erro ao registrar despesa.");
    } finally {
      setPhotoSubmitting(false);
    }
  }, [visao]);

  return (
    <div className="space-y-6 md:space-y-8" data-testid="finance-visao">
      {/* Linha 0a — botão "+ Por foto" (A2). Único ponto de criação
          de despesa na tela hoje, então fica em destaque no topo. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-caption text-foreground-subtle">
          Quick action — captura por foto.
        </p>
        <motion.button
          type="button"
          onClick={() => { setPhotoExtracted(null); setPhotoStep("capture"); }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-cyan/10 border border-brand-cyan/30 text-brand-cyan hover:bg-brand-cyan/15 transition-all text-sm font-medium"
          data-testid="add-expense-by-photo-btn"
          title="Capturar despesa por foto"
        >
          <Camera className="w-4 h-4" /> Despesa por foto
        </motion.button>
      </div>

      {/* Linha 0 — Sugestão proativa (omitida se vazio) */}
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.0 }}
      >
        <SugestaoNexoVisaoCard briefing={briefing} onTalk={onTalk} />
      </motion.section>

      {/* Linha 1 — Hero saldo + 3 mini-cards */}
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.08 }}
      >
        <HeroSaldoCard
          totalBalance={visao.totalBalance}
          summary={visao.summary}
          loading={visao.loading.totalBalance || visao.loading.summary}
        />
      </motion.section>

      {/* Linha 2 — Atividade + Assinaturas + Faturas (3 col) */}
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.16 }}
        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
      >
        <AtividadeRecenteCard
          recent={visao.recent}
          loading={visao.loading.recent}
          onSeeAll={() => { window.location.hash = "financeiro/extrato"; }}
        />
        <AssinaturasResumoCard
          onSeeAll={() => { window.location.hash = "financeiro/assinaturas"; }}
        />
        <FaturasCard
          onSeeAll={() => { window.location.hash = "financeiro/contas"; }}
          onConfigureCards={() => { window.location.hash = "financeiro/contas"; }}
        />
      </motion.section>

      {/* Linha 3 — Fluxo de Caixa (BarChart 6m) + Donut Distribuição */}
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.24 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        <FluxoCaixaChart
          cashflow={visao.cashflow}
          loading={visao.loading.cashflow}
          onSeeDetails={() => { window.location.hash = "financeiro/extrato"; }}
        />
        <DistribuicaoGastosDonut
          summary={visao.summary}
          loading={visao.loading.summary}
          onSeeMore={() => { window.location.hash = "financeiro/categorias"; }}
          onTalk={onTalk}
        />
      </motion.section>

      {/* Linha 4 — Ritmo cumulativo + Heatmap diário */}
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.32 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        <RitmoGastosLineChart
          cumulative={visao.cumulative}
          loading={visao.loading.cumulative}
          onSeeAll={() => { window.location.hash = "financeiro/extrato"; }}
        />
        <MapaGastosHeatmap
          heatmap={visao.heatmap}
          loading={visao.loading.heatmap}
          onFetchDayDetails={visao.fetchDayDetails}
          onSeeAllDay={(date) => {
            // Pré-filtro futuro: por enquanto só navega
            window.location.hash = `financeiro/extrato`;
            console.log("[finance] day filter pendente:", date);
          }}
        />
      </motion.section>

      {/* A2 — modal stepper Capture → Confirm */}
      <AnimatePresence>
        {photoStep && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closePhoto}
            data-testid="receipt-modal"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md"
            >
              {photoStep === "capture" && (
                <ReceiptCapture
                  onExtracted={(ex) => { setPhotoExtracted(ex); setPhotoStep("confirm"); }}
                  onClose={closePhoto}
                />
              )}
              {photoStep === "confirm" && photoExtracted && (
                <ReceiptConfirm
                  extracted={photoExtracted}
                  isSubmitting={photoSubmitting}
                  onCancel={closePhoto}
                  onSubmit={registerPhotoExpense}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
